import cn from 'classnames';
import {
  type ChangeEvent,
  type FC,
  type FocusEvent,
  type KeyboardEvent,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import Radio from '@/components/UI/FormElements/Radio';
import { newLineText, preventNumberScroll } from '@/helpers';
import { _isBlank, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import { isMacPlatform } from '@/utils/browser.utils';

import { severityScale } from './AddObservation.utils';

const MAX_COMMENT_LENGTH = 5000;

type ObservationFormType = 'none' | 'numeric' | 'severity';

interface ObservationFormObservation {
  title?: string | null;
  description?: string | null;
  type?: ObservationFormType | null;
  text: string;
  value?: string | number | null;
  options?: TeamGlossaryV1['options'] | { min?: number | string; max?: number | string; details?: Array<string> };
}

type ObservationFormErrors = false | null | Record<string, Array<string>>;

interface ObservationFormProps {
  observation: ObservationFormObservation;
  errors: ObservationFormErrors;
  index: number;
  handleInput: (index: number, key: 'text' | 'value', val: string | number | null) => void;
  width?: string | number;
  dispatch?: (action: { type: 'stepReady'; data?: boolean }) => void;
}

interface ObservationFieldProps {
  observation: ObservationFormObservation;
  errors: ObservationFormErrors;
  index: number;
  handleInput: ObservationFormProps['handleInput'];
  onScoreFieldKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

interface NumericScaleProps extends ObservationFieldProps {
  valueInputRef: RefObject<HTMLInputElement | null>;
}

interface SeverityOptionsProps extends ObservationFieldProps {
  selectedSeverityRef: RefObject<HTMLInputElement | null>;
}

const focusAndSelectValueInput = (input: HTMLInputElement | null) => {
  if (_isNil(input)) {
    return;
  }
  input.focus();
  input.select();
};

const resolveSelectedSeverity = (value: ObservationFormObservation['value']): string =>
  typeof value === 'string' && _isBlank(value) === false ? value : severityScale[0];

/**
 * Renders a single observation step form with type-specific scoring controls and comments.
 */
const ObservationForm: FC<ObservationFormProps> = ({ observation, errors, index, handleInput, width, dispatch }) => {
  const valueInputRef = useRef<HTMLInputElement | null>(null);
  const selectedSeverityRef = useRef<HTMLInputElement | null>(null);
  const commentsTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingCaretPositionRef = useRef<number | null>(null);

  const focusComments = () => {
    commentsTextareaRef.current?.focus();
  };

  const handleScoreFieldKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing === true) {
      return;
    }
    if (event.shiftKey === true || event.ctrlKey === true || event.metaKey === true) {
      return;
    }

    // Keep StepForm from finishing; move to comments for the Enter → Enter → Enter path.
    event.preventDefault();
    event.stopPropagation();
    focusComments();
  };

  const contentProps: ObservationFieldProps = {
    observation,
    index,
    handleInput,
    errors,
    onScoreFieldKeyDown: handleScoreFieldKeyDown,
  };

  useEffect(() => {
    if (_notNil(dispatch)) {
      dispatch({ type: 'stepReady' });
    }
  }, [dispatch]);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      if (observation.type === 'numeric') {
        focusAndSelectValueInput(valueInputRef.current);
        return;
      }

      if (observation.type === 'severity') {
        selectedSeverityRef.current?.focus();
        return;
      }

      focusComments();
    });

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [observation.type]);

  useLayoutEffect(() => {
    const caretPosition = pendingCaretPositionRef.current;
    if (_isNil(caretPosition)) {
      return;
    }
    pendingCaretPositionRef.current = null;
    commentsTextareaRef.current?.setSelectionRange(caretPosition, caretPosition);
  }, [observation.text]);

  const handleCommentsKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing === true) {
      return;
    }

    // Shift+Enter keeps the browser's native newline behaviour; plain Enter belongs to StepForm.
    if (event.shiftKey === true || !(event.ctrlKey === true || event.metaKey === true)) {
      return;
    }

    // Ctrl/Cmd+Enter has no native newline behaviour, so the character is inserted manually.
    event.preventDefault();
    event.stopPropagation();

    const { selectionStart, selectionEnd, value } = event.currentTarget;
    if (_isNil(selectionStart) || _isNil(selectionEnd)) {
      return;
    }

    const nextValue = `${value.slice(0, selectionStart)}\n${value.slice(selectionEnd)}`;
    if (nextValue.length > MAX_COMMENT_LENGTH) {
      return;
    }

    pendingCaretPositionRef.current = selectionStart + 1;
    handleInput(index, 'text', nextValue);
  };

  const newlineShortcutLabel = isMacPlatform() === true ? 'Cmd + Enter' : 'Ctrl + Enter';
  const hasDescription = _notNil(observation.description) && _isBlank(observation.description) === false;
  const commentsFieldId = `observation-comments-${index}`;
  const commentsHintId = `observation-comments-newline-hint-${index}`;

  const renderTypeContent = () => {
    switch (observation.type) {
      case 'numeric':
        return <NumericScale {...contentProps} valueInputRef={valueInputRef} />;
      case 'severity':
        return <SeverityOptions {...contentProps} selectedSeverityRef={selectedSeverityRef} />;
      default:
        return null;
    }
  };

  return (
    <div className="ph1" data-test-component="ObservationForm">
      {hasDescription && (
        <p className="lh-copy f6 mb3 measure-wide near-black mb3">{newLineText(observation.description)}</p>
      )}
      {renderTypeContent()}
      <div className="mt3 measure-wide">
        <label className="mb2" htmlFor={commentsFieldId}>
          Additional comments
        </label>
        <textarea
          ref={commentsTextareaRef}
          id={commentsFieldId}
          maxLength={MAX_COMMENT_LENGTH}
          style={{ width: width ?? '34em' }}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => handleInput(index, 'text', event.target.value)}
          onKeyDown={handleCommentsKeyDown}
          name="text"
          cols={30}
          rows={10}
          value={observation.text}
          data-testid="observation-comments"
          aria-describedby={commentsHintId}
        />
        <p id={commentsHintId} className="f7 gray lh-copy mb0">
          Press {newlineShortcutLabel} or Shift + Enter to start a new line
        </p>
      </div>
    </div>
  );
};

const NumericScale: FC<NumericScaleProps> = ({
  observation,
  errors,
  index,
  handleInput,
  valueInputRef,
  onScoreFieldKeyDown,
}) => {
  const fieldErrors = _notNil(errors) && errors !== false ? errors : null;
  const hasErrors = _notNil(fieldErrors);
  const valueErrors = hasErrors && _isNotEmpty(fieldErrors.value) ? fieldErrors.value : [];
  const minValue = observation.options?.min;
  const maxValue = observation.options?.max;
  const hasOptions = _notNil(observation.options);

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    event.currentTarget.select();
  };

  const valueFieldId = `observation-value-${index}`;

  return (
    <div className="mb3">
      <label htmlFor={valueFieldId}>
        Severity rating
        {hasOptions ? ` (${minValue}-${maxValue})` : ''}
      </label>
      <input
        ref={valueInputRef}
        id={valueFieldId}
        type="number"
        onWheel={preventNumberScroll}
        name="value"
        className={cn({ input__error: hasErrors })}
        style={{ marginBottom: 0, width: '125px' }}
        min={_notNil(minValue) ? minValue : undefined}
        max={_notNil(maxValue) ? maxValue : undefined}
        value={observation.value ?? ''}
        onChange={(event: ChangeEvent<HTMLInputElement>) => handleInput(index, 'value', event.target.value)}
        onFocus={handleFocus}
        onKeyDown={onScoreFieldKeyDown}
        data-testid="observation-numeric-value"
      />
      {_isNotEmpty(valueErrors) && (
        <ul className="f6 red lh-copy mt2">
          {valueErrors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const SeverityOptions: FC<SeverityOptionsProps> = ({
  observation,
  errors,
  index,
  handleInput,
  onScoreFieldKeyDown,
  selectedSeverityRef,
}) => {
  const fieldErrors = _notNil(errors) && errors !== false ? errors : null;
  const valueErrors = _notNil(fieldErrors) && _isNotEmpty(fieldErrors.value) ? fieldErrors.value : [];
  const selectedSeverity = resolveSelectedSeverity(observation.value);

  return (
    <div className="mb3" onKeyDown={onScoreFieldKeyDown} data-testid="observation-severity-options">
      <label className="mb2">Select a severity</label>
      {severityScale.map((severity) => (
        <Radio
          key={severity}
          ref={severity === selectedSeverity ? selectedSeverityRef : undefined}
          checked={observation.value === severity}
          onChange={(event: ChangeEvent<HTMLInputElement>) => handleInput(index, 'value', event.target.value)}
          id={severity}
          name="severity"
          label={severity}
          value={severity}
          className="mb2"
          keyboardFocusable
          dataTestId={`observation-severity-${severity.toLowerCase()}`}
        />
      ))}
      {_isNotEmpty(valueErrors) && (
        <ul className="f6 red lh-copy">
          {valueErrors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ObservationForm;
